<script setup lang="ts">
/**
 * How much of a storage quota is used — a mailbox, a Drive mount: an optional
 * `label` line, the bar (NxProgressMeter, so nearing the quota warns and
 * exceeding it is an error) and the usage as text („1,2 GB von 5 GB belegt“)
 * with the share at the end of that line.
 *
 * `used` / `total` are bytes by default (formatted with the kit's
 * `formatBytes` in the app locale); `formatValue` formats anything else — a
 * message count. An unknown quota (`total` 0 or missing) shows the usage
 * alone: no bar, no share. Without a `used` value the meter renders nothing,
 * so a sidebar foot without a quota keeps no empty band. `loading` shows a
 * skeleton in the meter's place (`aria-busy`).
 *
 * The texts are props with German defaults; `usageText` builds the line from
 * the formatted values, so an app passes its own translation.
 */
import { computed } from 'vue';
import { useNxFormat } from '../Format/useNxFormat';
import NxSkeleton from '../Loading/NxSkeleton.vue';
import NxProgressMeter from './NxProgressMeter.vue';

const props = withDefaults(
  defineProps<{
    /** Used amount (bytes unless `formatValue` says otherwise); `null` = unknown. */
    used?: number | null;
    /** The quota; 0 or `null` = no known limit (usage only, no bar). */
    total?: number | null;
    /** Visible line above the bar („Postfach“, a mount name); also the bar's name. */
    label?: string;
    loading?: boolean;
    /** Formats `used` and `total`. Defaults to `formatBytes` in the app locale. */
    formatValue?: (value: number) => string;
    /**
     * The bar colour: `auto` (default) warns from 85 % and turns red over the
     * quota; a literal variant pins it (a server with its own warn limit).
     */
    variant?: 'auto' | 'ok' | 'warn' | 'over';
    /**
     * The usage line from the formatted values; `total` is `null` without a
     * known quota. Default: „{used} von {total} belegt“ / „{used} belegt“.
     */
    usageText?: (used: string, total: string | null) => string;
    /** Accessible name of the skeleton region while loading. */
    loadingLabel?: string;
  }>(),
  {
    used: null,
    total: null,
    label: undefined,
    loading: false,
    formatValue: undefined,
    variant: 'auto',
    usageText: (used: string, total: string | null) =>
      total === null ? `${used} belegt` : `${used} von ${total} belegt`,
    loadingLabel: 'Speichernutzung wird geladen',
  }
);

const { formatBytes, formatPercent } = useNxFormat();

const format = (value: number) =>
  props.formatValue ? props.formatValue(value) : formatBytes(value);

const hasUsage = computed(() => typeof props.used === 'number' && Number.isFinite(props.used));
const usedValue = computed(() => (hasUsage.value ? Math.max(0, props.used as number) : 0));
const totalValue = computed(() =>
  typeof props.total === 'number' && Number.isFinite(props.total) && props.total > 0
    ? props.total
    : 0
);
const hasTotal = computed(() => totalValue.value > 0);

const text = computed(() =>
  props.usageText(format(usedValue.value), hasTotal.value ? format(totalValue.value) : null)
);
const share = computed(() =>
  hasTotal.value ? formatPercent(usedValue.value / totalValue.value, { maxDigits: 0 }) : ''
);
</script>

<template>
  <div
    v-if="loading"
    class="nx-usage-meter is-loading"
    aria-busy="true"
    :aria-label="props.loadingLabel"
    role="group"
    data-testid="nx-usage-meter"
  >
    <span v-if="props.label" class="nx-usage-meter__label">{{ props.label }}</span>
    <NxSkeleton variant="line" />
    <NxSkeleton variant="line" width="60%" />
  </div>
  <div v-else-if="hasUsage" class="nx-usage-meter" data-testid="nx-usage-meter">
    <span v-if="props.label" class="nx-usage-meter__label">{{ props.label }}</span>
    <NxProgressMeter
      v-if="hasTotal"
      :actual="usedValue"
      :target="totalValue"
      :format-value="format"
      :label="props.label || text"
      :variant="props.variant"
      :show-ratio="false"
      unit=""
    />
    <div class="nx-usage-meter__text">
      <span class="nx-usage-meter__usage">{{ text }}</span>
      <span v-if="share" class="nx-usage-meter__share" aria-hidden="true">{{ share }}</span>
    </div>
  </div>
</template>

<style scoped>
.nx-usage-meter {
  display: grid;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  font-size: var(--nxt-font-size-xs);
}

.nx-usage-meter__label {
  overflow: hidden;
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nx-usage-meter__text {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--nxt-space-xs);
  min-width: 0;
  color: var(--nxt-fg-muted);
  font-variant-numeric: tabular-nums;
}

.nx-usage-meter__usage {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nx-usage-meter__share {
  flex: 0 0 auto;
}
</style>
